import type { AccionDeAsunto, Asunto, EstadoDeAsunto, Prioridad } from '@/api/asuntos.api';

/**
 * LAS TRES VISTAS DE «ME TOCA» (tanda 6), en lo que tienen de PURO.
 *
 * Kervens: «necesito ver tres tipos de vistas para que el cliente escoja la que
 * es más amigable para él». La lista de siempre, los mismos asuntos por día
 * («¿qué hago ahora?») y un tablero con el flujo («¿en qué va cada cosa?»).
 * Aquí vive cómo se reparten; dibujarlos es de `components/metoca`.
 */

export type VistaDeMeToca = 'lista' | 'dias' | 'tablero';
export const VISTAS_DE_ME_TOCA: readonly VistaDeMeToca[] = ['lista', 'dias', 'tablero'];

export interface GrupoDeAsuntos {
  id: string;
  titulo: string;
  tono: 'alerta' | 'hoy' | 'normal' | 'apagado';
  asuntos: Asunto[];
}

type Molde = Pick<GrupoDeAsuntos, 'id' | 'titulo' | 'tono'>;

/** Urgente, importante, puede esperar; lo que no se ha revisado (o es ruido) va al final. */
const PESO: Partial<Record<Prioridad, number>> = { ALTA: 0, MEDIA: 1, BAJA: 2 };
const peso = (a: Asunto): number => PESO[a.prioridad] ?? 3;

/**
 * Cuándo toca: la fecha del compromiso; si no tiene, la fecha límite. `null` si ninguna sirve.
 * Lo pospuesto no toca antes de volver (`pospuestoHasta`): posponer no mueve la fecha del
 * compromiso, y por esa fecha vieja lo que acababa de volver caía en «Vencidos». Si su fecha
 * es todavía más adelante, manda su fecha.
 */
export function fechaDe(a: Asunto): Date | null {
  const valida = (iso: string | null): Date | null => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  };
  const fecha = valida(a.fechaCompromiso) ?? valida(a.fechaLimite);
  const vuelve = a.estado === 'POSPUESTO' ? valida(a.pospuestoHasta) : null;
  return vuelve && (!fecha || vuelve > fecha) ? vuelve : fecha;
}

const DIAS: Molde[] = [
  { id: 'vencidos', titulo: 'Vencidos', tono: 'alerta' },
  { id: 'hoy', titulo: 'Hoy', tono: 'hoy' },
  { id: 'manana', titulo: 'Mañana', tono: 'normal' },
  { id: 'semana', titulo: 'Esta semana', tono: 'normal' },
  { id: 'adelante', titulo: 'Más adelante', tono: 'normal' },
  { id: 'sin-fecha', titulo: 'Sin fecha', tono: 'apagado' },
];

/**
 * Vencidos, Hoy, Mañana, Esta semana, Más adelante, Sin fecha. Solo los grupos
 * que tienen algo, en ese orden.
 *
 * Los días se cuentan en la hora del navegador: «hoy» es el día de quien mira.
 * «Vencido» es lo que el servidor dio por vencido (algo de hoy a las 9 ya lo
 * está a las 2) o lo de un día anterior. «Esta semana» va de pasado mañana al
 * domingo; un sábado o un domingo ya no le queda nada.
 */
export function porDia(asuntos: Asunto[], ahora: Date): GrupoDeAsuntos[] {
  // Medianoche local de dentro de `mas` días; el calendario se encarga del cambio de mes y de hora.
  const dia = (mas: number) => new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + mas).getTime();
  const hoy = dia(0);
  const manana = dia(1);
  const pasado = dia(2);
  // El lunes que viene (`getDay()`: 0 es domingo).
  const lunes = dia(((7 - ahora.getDay()) % 7) + 1);

  const grupoDe = (a: Asunto): string => {
    const t = fechaDe(a)?.getTime();
    if (t === undefined) return 'sin-fecha';
    if (a.vencido || t < hoy) return 'vencidos';
    if (t < manana) return 'hoy';
    if (t < pasado) return 'manana';
    return t < lunes ? 'semana' : 'adelante';
  };
  // Por fecha y, a la misma hora, lo más urgente primero. (Sin fecha: Infinity − Infinity no es un número, y decide la urgencia.)
  const orden = (x: Asunto, y: Asunto) =>
    (fechaDe(x)?.getTime() ?? Infinity) - (fechaDe(y)?.getTime() ?? Infinity) || peso(x) - peso(y);

  return DIAS.map((g) => ({ ...g, asuntos: asuntos.filter((a) => grupoDe(a) === g.id).sort(orden) })).filter(
    (g) => g.asuntos.length > 0,
  );
}

const FLUJO: Array<Molde & { estados: EstadoDeAsunto[] }> = [
  { id: 'por-aprobar', titulo: 'Por aprobar', tono: 'normal', estados: ['DETECTADO'] },
  // Lo pospuesto que ya volvió (el servidor solo lo trae cuando llegó su hora) es otra vez algo por hacer.
  { id: 'por-hacer', titulo: 'Por hacer', tono: 'hoy', estados: ['APROBADO', 'ASIGNADO', 'PROGRAMADO', 'POSPUESTO'] },
  { id: 'esperando', titulo: 'Esperando respuesta', tono: 'normal', estados: ['AVISADO', 'RESPONDIDO'] },
  { id: 'vencido', titulo: 'Vencido', tono: 'alerta', estados: ['VENCIDO'] },
];

/**
 * En qué columna del tablero va, o `null` si su estado no es del flujo
 * (cumplido, descartado, ignorado…): esos no salen en el tablero, aunque el
 * servidor los marque `vencido` por su fecha.
 */
export function columnaDe(a: Asunto, ahora: Date = new Date()): string | null {
  const suya = FLUJO.find((c) => c.estados.includes(a.estado));
  if (!suya) return null;
  // Lo pospuesto para MÁS ADELANTE todavía no toca: la lista abierta no lo trae, pero «Ruido»
  // pide todos los estados y por ahí llegaría como tarea activa (revisión de Codex).
  if (a.estado === 'POSPUESTO' && a.pospuestoHasta && new Date(a.pospuestoHasta).getTime() > ahora.getTime()) return null;
  // Un vencido va SOLO en «Vencido»: verlo dos veces sería contar dos tareas.
  return a.vencido ? 'vencido' : suya.id;
}

/** Por aprobar, Por hacer, Esperando respuesta, Vencido. Las cuatro columnas SIEMPRE, aunque estén vacías. */
export function porFlujo(asuntos: Asunto[], ahora: Date = new Date()): GrupoDeAsuntos[] {
  return FLUJO.map(({ id, titulo, tono }) => ({ id, titulo, tono, asuntos: asuntos.filter((a) => columnaDe(a, ahora) === id) }));
}

export type AccionPrincipal = 'aprobar' | 'hecho' | 'revisar';

/**
 * Qué hace el botón principal de un asunto, en la tarjeta y en la hoja:
 * «aprobar» si todavía es una suposición de la máquina; si no, «hecho».
 *
 * «revisar» es para un mensaje al cliente (un borrador de Seguimientos): aquí
 * no se ve el texto y aprobarlo lo manda por WhatsApp, así que ni se aprueba ni
 * se da por hecho; se va a Seguimientos a leerlo. El servidor tampoco lo deja.
 *
 * Existe por un fallo: la tarjeta rotulaba «Hecho» lo ya aprobado, pero las
 * pantallas mandaban siempre `aprobar`, y nada quedaba cumplido nunca.
 */
export function accionPrincipal(a: Asunto): AccionPrincipal {
  if (a.esBorrador) return 'revisar';
  return a.estado === 'DETECTADO' ? 'aprobar' : 'hecho';
}

/**
 * El botón principal de la hoja del asunto: lo cerrado y lo que es ruido se
 * rescata; lo demás, lo mismo que en la tarjeta.
 */
export function botonDeLaHoja(a: Asunto): AccionPrincipal | 'rescatar' {
  if (a.estado === 'CUMPLIDO' || a.estado === 'DESCARTADO' || a.prioridad === 'RUIDO') return 'rescatar';
  return accionPrincipal(a);
}

/**
 * Lo único que hace soltar una tarjeta del tablero: de «Por aprobar» a «Por
 * hacer», aprobarla. Cualquier otro arrastre no hace nada (`null`), y un
 * mensaje para el cliente no se arrastra: arrastrarlo sería aprobarlo sin leerlo.
 */
export function alSoltar(a: Asunto, destino: string): 'aprobar' | null {
  if (a.esBorrador) return null;
  return columnaDe(a) === 'por-aprobar' && destino === 'por-hacer' ? 'aprobar' : null;
}

/**
 * Con qué acción se deshace otra, o `null` si no se ofrece «Deshacer».
 *
 * Deshacer es volver a como estaba, no «aprobar»: antes, deshacer un «Hecho»
 * mandaba `aprobar` siempre, también sobre algo que nadie había aprobado.
 *  - hecho: vuelve a aprobado SOLO si ya lo estaba; desde otro estado no hay vuelta exacta.
 *  - descartar ↔ rescatar: uno deshace al otro.
 *  - aprobar (y lo demás): no se deshace.
 */
export function deshacerDe(accion: AccionDeAsunto, estadoAnterior: EstadoDeAsunto): AccionDeAsunto | null {
  if (accion === 'hecho') return estadoAnterior === 'APROBADO' ? 'aprobar' : null;
  if (accion === 'descartar') return 'rescatar';
  if (accion === 'rescatar') return 'descartar';
  return null;
}

/**
 * Cómo queda la lista en pantalla apenas se toca, sin esperar al servidor (si
 * el servidor falla, la pantalla vuelve a la de antes). Lo aprobado deja de ser
 * suposición; lo hecho y lo descartado salen de la lista. Lo demás no se
 * adelanta: espera la respuesta.
 */
export function alInstante(asuntos: Asunto[], id: string, accion: AccionDeAsunto): Asunto[] {
  if (accion === 'aprobar') {
    return asuntos.map((a) => (a.id === id ? { ...a, estado: 'APROBADO', esSuposicion: false } : a));
  }
  if (accion === 'hecho' || accion === 'descartar') return asuntos.filter((a) => a.id !== id);
  return asuntos;
}
